export type ModalStackBehavior = "suspend" | "stack";

export interface ModalStackEntry {
  id: string;
  behavior: ModalStackBehavior;
  status: "open" | "closing";
}

export type ModalStackAction =
  | { type: "activate"; id: string; behavior: ModalStackBehavior }
  | { type: "beginClose"; id: string }
  | { type: "deactivate"; id: string; force?: boolean };

export function getModalPresentation(entries: ModalStackEntry[], id: string) {
  const index = entries.findIndex((entry) => entry.id === id);
  let firstVisibleIndex = entries.length - 1;

  while (
    firstVisibleIndex > 0 &&
    entries[firstVisibleIndex].behavior === "stack"
  ) {
    firstVisibleIndex -= 1;
  }

  return {
    registered: index !== -1,
    visible: index !== -1 && index >= firstVisibleIndex,
    topmost: index !== -1 && index === entries.length - 1,
    layer: Math.max(index, 0),
  };
}

export function modalStackReducer(
  entries: ModalStackEntry[],
  action: ModalStackAction,
): ModalStackEntry[] {
  const current = entries.find((entry) => entry.id === action.id);

  switch (action.type) {
    case "activate": {
      const next: ModalStackEntry = {
        id: action.id,
        behavior: action.behavior,
        status: "open",
      };

      if (!current || current.status === "closing") {
        return [...entries.filter((entry) => entry.id !== action.id), next];
      }

      if (current.behavior === action.behavior) return entries;

      return entries.map((entry) => (entry.id === action.id ? next : entry));
    }
    case "beginClose":
      if (!current || current.status === "closing") return entries;

      // Suspended dialogs have nothing visible to animate.
      if (!getModalPresentation(entries, action.id).visible) {
        return entries.filter((entry) => entry.id !== action.id);
      }

      return entries.map((entry) =>
        entry.id === action.id ? { ...entry, status: "closing" } : entry,
      );
    case "deactivate":
      if (!current) return entries;
      if (!action.force && current.status !== "closing") return entries;

      return entries.filter((entry) => entry.id !== action.id);
  }
}
